import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import { getUserEngagementInsights, getPromptAnalytics, PromptAnalytics } from '../../services/analyticsService';
import BarChart from '../charts/BarChart';

const ContentTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [contentData, setContentData] = useState<any>(null);
  const [promptAnalytics, setPromptAnalytics] = useState<PromptAnalytics | null>(null);
  const [promptLoading, setPromptLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get learning paths count
        const learningPaths = await getCollection('learning_paths', undefined, undefined, undefined, 10000).catch(() => []);
        
        // Get engagement insights for content analytics
        const engagementInsights = await getUserEngagementInsights().catch(() => null);

        setContentData({
          learningPathsCount: learningPaths.length,
          popularTopics: engagementInsights?.popularTopics || [],
          topProgressTopics: engagementInsights?.topProgressTopics || [],
        });
      } catch (error) {
        console.error('Error loading content data:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadPromptAnalytics = async () => {
      try {
        const analytics = await getPromptAnalytics(30);
        setPromptAnalytics(analytics);
      } catch (error) {
        console.error('Error loading prompt analytics:', error);
      } finally {
        setPromptLoading(false);
      }
    };

    loadData();
    loadPromptAnalytics();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading content data...</p>
      </div>
    );
  }

  if (!contentData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No content data available</p>
      </div>
    );
  }

  const popularTopicsData = {
    labels: contentData.popularTopics.slice(0, 10).map((t: any) => t.title.substring(0, 20)),
    datasets: [
      {
        label: 'Times Added',
        data: contentData.popularTopics.slice(0, 10).map((t: any) => t.count),
        backgroundColor: '#2563eb',
      },
    ],
  };

  const progressTopicsData = {
    labels: contentData.topProgressTopics.slice(0, 10).map((t: any) => t.title.substring(0, 20)),
    datasets: [
      {
        label: 'Avg Progress %',
        data: contentData.topProgressTopics.slice(0, 10).map((t: any) => t.averageProgress),
        backgroundColor: '#10b981',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Content Analytics
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Learning Paths</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {contentData.learningPathsCount}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Unique Topics</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {contentData.popularTopics.length}
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Most Popular Topics</h3>
          <BarChart data={popularTopicsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Highest Progress Topics</h3>
          <BarChart data={progressTopicsData} height={300} />
        </div>
      </div>

      {/* Top Topics Table */}
      {contentData.topProgressTopics.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Top Performing Topics
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Topic</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Avg Progress</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Completions</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Times Added</th>
              </tr>
            </thead>
            <tbody>
              {contentData.topProgressTopics.slice(0, 10).map((topic: any, index: number) => {
                const popularData = contentData.popularTopics.find((p: any) => p.title === topic.title);
                return (
                  <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '1rem' }}>{topic.title}</td>
                    <td style={{ padding: '1rem' }}>
                      {topic.averageProgress.toFixed(1)}%
                    </td>
                    <td style={{ padding: '1rem' }}>{topic.completionCount}</td>
                    <td style={{ padding: '1rem' }}>{popularData?.count || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Prompt Analytics Section */}
      <div style={{ marginTop: '2rem' }}>
        <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
          Prompt Analytics
        </h2>
        
        {promptLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading prompt analytics...</p>
          </div>
        ) : promptAnalytics ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '1.5rem',
          }}>
            {/* Age Band Distribution */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ marginTop: 0 }}>Age Band Distribution</h3>
              {Object.keys(promptAnalytics.ageBandDistribution).length > 0 ? (
                <div>
                  {Object.entries(promptAnalytics.ageBandDistribution).map(([ageBand, count]) => {
                    const total = Object.values(promptAnalytics.ageBandDistribution).reduce((a: number, b: number) => a + b, 0);
                    const percentage = total > 0 ? ((count as number) / total * 100) : 0;
                    return (
                      <div key={ageBand} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span>{formatAgeBand(ageBand)}</span>
                          <span style={{ fontWeight: 'bold' }}>
                            {count} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: '#2563eb',
                            transition: 'width 0.3s',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#666' }}>No age band data available</p>
              )}
            </div>

            {/* Blending Preference Usage */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ marginTop: 0 }}>Blending Preference Usage</h3>
              {Object.keys(promptAnalytics.blendingPreferenceUsage).length > 0 ? (
                <div>
                  {Object.entries(promptAnalytics.blendingPreferenceUsage).map(([pref, count]) => {
                    const total = Object.values(promptAnalytics.blendingPreferenceUsage).reduce((a: number, b: number) => a + b, 0);
                    const percentage = total > 0 ? ((count as number) / total * 100) : 0;
                    return (
                      <div key={pref} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span>{formatBlendingPreference(pref)}</span>
                          <span style={{ fontWeight: 'bold' }}>
                            {count} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: '#10b981',
                            transition: 'width 0.3s',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#666' }}>No blending preference data available</p>
              )}
            </div>

            {/* Category Generation Metrics */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ marginTop: 0 }}>Category Generation Metrics</h3>
              <div style={{ marginTop: '1rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Total Generations: </span>
                  <span style={{ fontWeight: 'bold' }}>
                    {promptAnalytics.categoryGenerationMetrics.totalGenerations}
                  </span>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Average Generation Time: </span>
                  <span style={{ fontWeight: 'bold' }}>
                    {promptAnalytics.categoryGenerationMetrics.averageGenerationTimeMs > 0
                      ? `${(promptAnalytics.categoryGenerationMetrics.averageGenerationTimeMs / 1000).toFixed(1)}s`
                      : 'N/A'}
                  </span>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Success Rate: </span>
                  <span style={{ 
                    fontWeight: 'bold',
                    color: promptAnalytics.categoryGenerationMetrics.successRate >= 95 
                      ? '#10b981' 
                      : promptAnalytics.categoryGenerationMetrics.successRate >= 80 
                        ? '#f59e0b' 
                        : '#ef4444'
                  }}>
                    {promptAnalytics.categoryGenerationMetrics.successRate.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Topic Generation Metrics */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <h3 style={{ marginTop: 0 }}>Topic Generation Metrics</h3>
              <div style={{ marginTop: '1rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Total Generations: </span>
                  <span style={{ fontWeight: 'bold' }}>
                    {promptAnalytics.topicGenerationMetrics.totalGenerations}
                  </span>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Load More Usage: </span>
                  <span style={{ fontWeight: 'bold' }}>
                    {promptAnalytics.topicGenerationMetrics.loadMoreUsage.load_more} load more,{' '}
                    {promptAnalytics.topicGenerationMetrics.loadMoreUsage.initial} initial
                  </span>
                </div>
                {Object.keys(promptAnalytics.topicGenerationMetrics.byCategory).length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>By Category:</p>
                    {Object.entries(promptAnalytics.topicGenerationMetrics.byCategory)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .slice(0, 5)
                      .map(([category, count]) => (
                        <div key={category} style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                          <span>{category}: </span>
                          <span style={{ fontWeight: 'bold' }}>{count as number}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            <p>No prompt analytics data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

const formatAgeBand = (ageBand: string): string => {
  switch (ageBand) {
    case 'younger_teens':
      return 'Younger Teens (13-15)';
    case 'older_teens_young_adults':
      return 'Older Teens/Young Adults (16-22)';
    case 'early_mid_career':
      return 'Early/Mid Career (23-39)';
    case 'experienced_adults':
      return 'Experienced Adults (40-59)';
    case 'older_lifelong_learners':
      return 'Older Lifelong Learners (60+)';
    default:
      return ageBand;
  }
};

const formatBlendingPreference = (preference: string): string => {
  switch (preference) {
    case 'separate':
      return 'Separate Categories';
    case 'blended':
      return 'Blended Categories';
    case 'both':
      return 'Mixed (Both)';
    default:
      return preference;
  }
};

export default ContentTab;

